import React, { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { reduce } from 'lodash';
import numeral from 'numeral';
import randomColor from 'randomcolor';
import { sortPlatforms, formatToPercent, formatToNumber } from '../../../utils/shared';

const barSecondaryTextColor = '#888888';
const textColor = '#666666';
const barHeight = 56;
const renderLogo = (name = '', logoValue = '', padding = {}) => {
  const isImage = logoValue.startsWith('https://');
  const { x, y } = padding;
  let logo;
  if (isImage) {
    logo = <image x="30" y="5" height={30} width={30} href={logoValue} />;
  } else {
    logo = (
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={textColor}
        style={{
          fontSize: 18,
          textAlign: 'left',
          textTransform: 'uppercase',
          fontWeight: 'bold'
        }}
      >
        {logoValue}
      </text>
    );
  }
  return logo;
};
const Tooltip = ({ currentBar, color }) => {
  const { voteCount, voteShare, platformName } = currentBar.data;
  return (
    <div className="tvt-timeline__tooltip">
      <div className="tvt-timeline__tooltip-data-platform">
        <div className="tvt-timeline__tooltip-data-platform-item tvt-timeline__tooltip-data-platform-header">
          <div
            className="tvt-timeline__tooltip-data-color tvt-timeline__tooltip-data-platform-color"
            style={{ backgroundColor: color }}
          />
          {platformName}
        </div>
        <div className="tvt-timeline__tooltip-data-platform-item">
          <span className="tvt-timeline__tooltip-data-platform-item-label">
            % Viewers:
          </span>
          {formatToPercent(voteShare)}
        </div>
        <div className="tvt-timeline__tooltip-data-platform-item">
          <span className="tvt-timeline__tooltip-data-platform-item-label">
            Viewers:
          </span>
          {formatToNumber(voteCount)}
        </div>
      </div>
    </div>
  );
};
const CustomRightBar = props => {
  const { x, y, width, height, color, data, showTooltip, hideTooltip } = props;
  const { voteCount, voteShare } = data.data;
  return (
    <g
      transform={`translate(${x}, ${y})`}
      onMouseMove={event => {
        showTooltip(<Tooltip currentBar={data} color={color} />, event);
      }}
      onMouseLeave={() => {
        hideTooltip();
      }}
    >
      <rect
        width={width}
        height={height}
        fill={color}
        strokeWidth="5"
        stroke="#fff"
      />
      <text x={width + 5} y={15} textAnchor="start" dominantBaseline="central">
        <tspan fill={textColor} className="platform-chart__bar-primary-label">
          {formatToPercent(voteShare)}
        </tspan>
      </text>
      <text x={width + 5} y={35} textAnchor="start" dominantBaseline="central">
        <tspan
          fill={barSecondaryTextColor}
          className="platform-chart__bar-secondary-label"
        >
          {formatToNumber(voteCount)}
        </tspan>
      </text>
    </g>
  );
};
const createRenderTick = nameLogoMapping => tick => {
  const name = tick.value;
  const labels = splitText(name, 16);
  const isPadding = labels.length > 1;
  const padding =  isPadding ? {x: 42.5, y: 19} : {x: 42.5, y: 25};
  const logo = renderLogo(name, nameLogoMapping[name], padding);
  return (
    <g transform={`translate(${tick.x - 100},${tick.y - 28})`}>
      <rect x="0" y="0" width="85" height="56" fill="transparent" />
      {logo}
      <text x="42.5" y={isPadding ? 34 : 40} textAnchor="middle" dominantBaseline="middle">
        {renderLabels(labels)}
      </text>
    </g>
  );
};

const splitText = (text, maxWidth) => {
  const labels = [];
  let firstRowLabel = '';
  const words = text.split(' ');
  let i = 0;
  while (i < words.length) {
    const word = words[i];
    if (firstRowLabel.length + word.length > maxWidth)  {
      break;
    }
    firstRowLabel = firstRowLabel + word + ' ';
    i++;
  }
  labels.push(firstRowLabel.trim());
  let secondRowLabel = words.slice(i, words.length);
  if (secondRowLabel.length > 0) {
    labels.push(secondRowLabel.join(' '));
  }
  return labels;
}

const renderLabels = (labels = []) => {
  return labels.length > 1 ? (
    <>
      <tspan fill={textColor} className="platform-chart__tick-label">
        {labels[0]}
      </tspan>
      <tspan x="42.5" y="45"  fill={textColor} className="platform-chart__tick-label">
        {labels[1]}
      </tspan>
    </>
  ) : (
    <tspan fill={textColor} className="platform-chart__tick-label">
      {labels[0]}
    </tspan>
  );
}

const sampleSize = d =>
  d.reduce((memo, current) => {
    return current.sampleSize;
  }, 0);

const PlatformLoadingIndicator = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="531"
    height="276"
    viewBox="0 0 531 276"
  >
    <defs>
      <linearGradient id="bsfsdfnexa" x1="0%" x2="100%" y1="50%" y2="50%">
        <stop offset="0%" stopColor="#EEEEEE" />
        <stop offset="33%" stopColor="#EEEEEE" />
        <stop offset="50%" stopColor="#f5f5f5" />
        <stop offset="67%" stopColor="#EEEEEE" />
        <stop offset="100%" stopColor="#EEEEEE" />
        <animateTransform
          attributeName="gradientTransform"
          type="translate"
          from="-1 0"
          to="1 0"
          begin="0s"
          dur="1.5s"
          repeatCount="indefinite"
        />
      </linearGradient>
    </defs>
    <g fill="none" fill-rule="evenodd">
      <g fill="url(#bsfsdfnexa)" transform="translate(-46.000000, -628.000000)">
        <g>
          <g>
            <path
              d="M123 10v10h358v36H123v20h40v36h-40v20h36v36h-36v20h36v36h-36v19h20v36h-20v20h10v36h-10v20h10v36h-10v39h-1V10h1zM98 380v14H26v-14h72zm-24-27v24H50v-24h24zm24-29v14H26v-14h72zm-24-27v24H50v-24h24zm24-29v14H26v-14h72zm-24-27v24H50v-24h24zm24-29v14H26v-14h72zm-24-27v24H50v-24h24zm24-29v14H26v-14h72zm-24-27v24H50v-24h24zm24-29v14H26v-14h72zM74 73v24H50V73h24zm25-29v14H27V44h72zM75 17v24H51V17h24z"
              transform="translate(20.000000, 579.000000) translate(0.000000, 39.000000)"
            />
          </g>
        </g>
      </g>
    </g>
  </svg>
);

const PlatformDemo = ({ data = [], showSize, loading }) => {
  const containerHeight = data.length * barHeight;
  const indexKey = 'platformName';
  const keys = ['voteCount'];

  const nameLogoMapping = useMemo(
    () =>
      reduce(
        data,
        (memo, { platformName, platformLogo }) => {
          if (!platformName) {
            return memo;
          }
          if (!platformLogo) {
            platformLogo =
              platformName.length > 3 ? platformName[0] : platformName;
          }
          memo[platformName] = platformLogo;
          return memo;
        },
        {}
      ),
    [data]
  );

  const platformColorMapping = useMemo(
    () =>
      reduce(
        data,
        (memo, { platformName }) => {
          memo[platformName] = randomColor({
            luminosity: 'dark',
            hue: 'random'
          });
          return memo;
        },
        {}
      ),
    [data]
  );

  const renderTick = createRenderTick(nameLogoMapping);
  const sortedPlatforms = sortPlatforms(data);

  return (
    <div className="platform-chart">
      {showSize && !loading && (
        <div className="tvt-chart__sample-size-num">
          {`N = ${numeral(sampleSize(data)).format(',')}`}
        </div>
      )}
      {!loading && (
        <div
          style={{
            height: containerHeight
          }}
          className="platform-chart-container"
        >
          <ResponsiveBar
            barComponent={props => {
              const { platformName } = props.data.data;
              const color = platformColorMapping[platformName];
              const newProps = { ...props, color };
              return <CustomRightBar {...newProps} />;
            }}
            labelTextColor="#373737"
            isInteractive={true}
            height={containerHeight}
            valueScale={{ type: "symlog" }}
            axisLeft={{
              tickSize: 0,
              renderTick
            }}
            axisRight={null}
            padding={0.3}
            margin={{
              top: 0,
              right: 70,
              bottom: 0,
              left: 120
            }}
            enableGridY={false}
            layout="horizontal"
            data={sortedPlatforms}
            indexBy={indexKey}
            keys={keys}
            markers={[
              {
                axis: 'x',
                value: 0,
                lineStyle: { stroke: 'rgba(0, 0, 0, .35)', strokeWidth: 1 }
              }
            ]}
          />
        </div>
      )}
      {loading && (
        <div className="platform-loading-container">
          <PlatformLoadingIndicator />
        </div>
      )}
    </div>
  );
};

PlatformDemo.defaultProps = {
  data: [],
  loading: false,
  showSize: true
};

export default PlatformDemo;
